(() => {
  'use strict';

  const SELECTOR = '[data-nolia-quiz]';

  const shuffle = (items) => {
    const copy = [...items];
    const random = new Uint32Array(copy.length || 1);
    if (window.crypto?.getRandomValues) window.crypto.getRandomValues(random);

    for (let index = copy.length - 1; index > 0; index -= 1) {
      const seed = window.crypto?.getRandomValues ? random[index] : Math.floor(Math.random() * 2 ** 32);
      const swapIndex = seed % (index + 1);
      [copy[index], copy[swapIndex]] = [copy[swapIndex], copy[index]];
    }
    return copy;
  };

  const safeJson = (element, fallback) => {
    try {
      return JSON.parse(element?.textContent || '');
    } catch (error) {
      console.error('Noliaquiz: kunde inte läsa konfigurationen.', error);
      return fallback;
    }
  };

  const analyticsAllowed = () => {
    try {
      if (window.Shopify?.customerPrivacy?.analyticsProcessingAllowed) {
        return Boolean(window.Shopify.customerPrivacy.analyticsProcessingAllowed());
      }
    } catch (error) {
      console.warn('Noliaquiz: kunde inte läsa cookie-medgivande.', error);
    }
    return false;
  };

  const pushEvent = (name, payload = {}) => {
    if (!analyticsAllowed()) return;
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({ event: name, ...payload });
    window.dispatchEvent(new CustomEvent(name, { detail: payload }));
  };

  const formatResultLevel = (score) => {
    if (score === 8) return 'Eldmästare';
    if (score >= 6) return 'Eldkunnig';
    if (score >= 4) return 'Värmesökare';
    return 'Gnistan är tänd';
  };

  const resultCopy = (level) => {
    const copy = {
      Eldmästare: 'Du har full koll på både vedeldning, Hans Forsman och våra produkter. Fyll i dina uppgifter för att delta i utlottningen av en Rizzoli AG Grill.',
      Eldkunnig: 'Du har god koll på både vedeldning och våra produkter. Fyll i dina uppgifter för att delta i utlottningen av en Rizzoli AG Grill.',
      Värmesökare: 'Du är på god väg och har fått med dig några nya glödande kunskaper. Fyll i dina uppgifter för att delta i utlottningen av en Rizzoli AG Grill.',
      'Gnistan är tänd': 'Nu är gnistan tänd – och du är välkommen att upptäcka mer på eldstad.se. Fyll i dina uppgifter för att delta i utlottningen av en Rizzoli AG Grill.'
    };
    return copy[level] || copy['Gnistan är tänd'];
  };

  class NoliaQuiz {
    constructor(root) {
      this.root = root;
      this.sectionId = root.dataset.sectionId || 'nolia';
      this.endpoint = root.dataset.endpoint || '';
      this.startAt = new Date(root.dataset.startAt);
      this.endAt = new Date(root.dataset.endAt);
      this.kiosk = new URLSearchParams(window.location.search).get('kiosk') === '1';
      this.kioskResetSeconds = Number(root.dataset.kioskResetSeconds || 30);
      this.winnerMode = root.dataset.winnerMode === 'true';
      this.questions = shuffle(safeJson(root.querySelector('[data-questions-json]'), []));
      this.state = { index: 0, score: 0, answered: false, completed: false };
      this.kioskTimer = null;
      this.formStarted = false;

      this.elements = {
        startButton: root.querySelector('[data-start-quiz]'),
        preOpen: root.querySelector('[data-pre-open]'),
        closed: root.querySelector('[data-closed]'),
        live: root.querySelector('[data-live-region]'),
        quizSection: root.querySelector('[data-quiz-section]'),
        questionCard: root.querySelector('[data-question-card]'),
        resultCard: root.querySelector('[data-result-card]'),
        thanksCard: root.querySelector('[data-thanks-card]'),
        progressLabel: root.querySelector('[data-progress-label]'),
        progressTrack: root.querySelector('[data-progress-track]'),
        progressBar: root.querySelector('[data-progress-bar]'),
        questionLegend: root.querySelector('[data-question-legend]'),
        answers: root.querySelector('[data-answers]'),
        feedback: root.querySelector('[data-feedback]'),
        nextButton: root.querySelector('[data-next-question]'),
        resultScore: root.querySelector('[data-result-score]'),
        resultTitle: root.querySelector('[data-result-title]'),
        resultCopy: root.querySelector('[data-result-copy]'),
        form: root.querySelector('[data-entry-form]'),
        formStatus: root.querySelector('[data-form-status]'),
        submitButton: root.querySelector('[data-submit-entry]'),
        nextParticipantButton: root.querySelector('[data-next-participant]')
      };

      this.init();
    }

    init() {
      this.bindWinnerEvents();

      if (this.winnerMode) {
        pushEvent('nolia_winner_view', { campaign: 'nolia_2026_vinnare' });
        return;
      }

      if (this.questions.length !== 8) {
        console.warn(`Noliaquiz: ${this.questions.length} frågor hittades. Exakt åtta rekommenderas.`);
      }

      this.applyCampaignState();
      this.captureUtmParameters();
      this.configureKioskMode();
      this.bindEvents();
      pushEvent('nolia_quiz_view', { campaign: 'nolia_pitea_2026_ag_grill' });
    }

    applyCampaignState() {
      const now = new Date();
      const startsLater = Number.isFinite(this.startAt.getTime()) && now < this.startAt;
      const hasEnded = Number.isFinite(this.endAt.getTime()) && now > this.endAt;

      this.elements.preOpen.hidden = !startsLater;
      this.elements.closed.hidden = !hasEnded;

      if (startsLater || hasEnded) {
        this.elements.startButton.hidden = true;
        this.elements.quizSection.hidden = true;
      } else {
        this.elements.startButton.hidden = false;
        this.elements.quizSection.hidden = true;
      }
    }

    configureKioskMode() {
      if (!this.kiosk || !this.elements.form) return;
      this.root.dataset.kiosk = 'true';
      this.elements.form.setAttribute('autocomplete', 'off');
      this.elements.form.querySelectorAll('input').forEach((input) => {
        if (!input.type || ['text', 'email', 'tel'].includes(input.type)) {
          input.setAttribute('autocomplete', 'off');
          input.setAttribute('autocorrect', 'off');
          input.setAttribute('spellcheck', 'false');
        }
      });
      if (this.elements.nextParticipantButton) this.elements.nextParticipantButton.hidden = false;
    }

    bindWinnerEvents() {
      this.root.querySelectorAll('[data-winner-internal-click]').forEach((link) => {
        link.addEventListener('click', () => {
          let destinationPath = '';
          try {
            destinationPath = new URL(link.href, window.location.origin).pathname;
          } catch (error) {
            destinationPath = link.getAttribute('href') || '';
          }

          pushEvent('nolia_winner_internal_click', {
            campaign: 'nolia_2026_vinnare',
            destination_path: destinationPath,
            link_text: (link.textContent || '').trim().slice(0, 120),
            link_type: link.dataset.linkType || 'internal_link'
          });
        });
      });
    }

    bindEvents() {
      this.elements.startButton?.addEventListener('click', () => this.start());
      this.elements.nextButton?.addEventListener('click', () => this.next());
      this.elements.form?.addEventListener('submit', (event) => this.submit(event));
      this.elements.form?.addEventListener('focusin', () => {
        if (!this.formStarted) {
          this.formStarted = true;
          pushEvent('nolia_quiz_form_start', { campaign: 'nolia_pitea_2026_ag_grill' });
        }
      }, { once: true });
      this.elements.nextParticipantButton?.addEventListener('click', () => this.resetForNextParticipant());
      this.root.querySelectorAll('[data-product-click]').forEach((link) => {
        link.addEventListener('click', () => pushEvent('nolia_quiz_product_click', { campaign: 'nolia_pitea_2026_ag_grill' }));
      });
      this.root.querySelectorAll('[data-retailer-click]').forEach((link) => {
        link.addEventListener('click', () => pushEvent('nolia_quiz_retailer_click', { campaign: 'nolia_pitea_2026_ag_grill' }));
      });
    }

    start() {
      if (!this.questions.length) {
        this.announce('Quizet kunde inte laddas. Försök igen eller kontakta personalen i montern.');
        return;
      }
      this.state = { index: 0, score: 0, answered: false, completed: false };
      this.elements.startButton.hidden = true;
      this.elements.quizSection.hidden = false;
      this.elements.questionCard.hidden = false;
      this.elements.resultCard.hidden = true;
      this.elements.thanksCard.hidden = true;
      this.renderQuestion();
      this.elements.quizSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
      pushEvent('nolia_quiz_start', { campaign: 'nolia_pitea_2026_ag_grill' });
    }

    renderQuestion() {
      const question = this.questions[this.state.index];
      if (!question) return this.complete();

      this.state.answered = false;
      this.elements.feedback.hidden = true;
      this.elements.feedback.innerHTML = '';
      this.elements.nextButton.hidden = true;
      this.elements.progressLabel.textContent = `Fråga ${this.state.index + 1} av ${this.questions.length}`;
      this.elements.progressBar.style.width = `${((this.state.index + 1) / this.questions.length) * 100}%`;
      this.elements.progressTrack?.setAttribute('aria-valuenow', String(this.state.index + 1));
      this.elements.progressTrack?.setAttribute('aria-valuemax', String(this.questions.length));
      this.elements.questionLegend.textContent = question.question;
      this.elements.answers.replaceChildren();

      shuffle(question.answers).forEach((answer, answerIndex) => {
        const wrapper = document.createElement('div');
        wrapper.className = 'nolia-quiz__answer';

        const input = document.createElement('input');
        input.type = 'radio';
        input.name = `${this.sectionId}-question-${this.state.index}`;
        input.id = `${this.sectionId}-q${this.state.index}-a${answerIndex}`;
        input.value = answer.key;

        const label = document.createElement('label');
        label.htmlFor = input.id;
        label.textContent = answer.text;

        input.addEventListener('change', () => this.answer(question, answer.key, wrapper));
        wrapper.append(input, label);
        this.elements.answers.append(wrapper);
      });

      window.setTimeout(() => {
        const firstInput = this.elements.answers.querySelector('input');
        firstInput?.focus({ preventScroll: true });
      }, 0);
    }

    answer(question, selectedKey, selectedWrapper) {
      if (this.state.answered) return;
      this.state.answered = true;

      const isCorrect = selectedKey === question.correct;
      if (isCorrect) this.state.score += 1;

      this.elements.answers.querySelectorAll('input').forEach((input) => {
        input.disabled = true;
        const wrapper = input.closest('.nolia-quiz__answer');
        if (input.value === question.correct) wrapper?.classList.add('is-correct');
        if (input.checked && !isCorrect) selectedWrapper?.classList.add('is-wrong');
      });

      const heading = isCorrect ? 'Rätt!' : 'Inte riktigt.';
      const feedbackText = isCorrect ? question.feedbackCorrect : question.feedbackWrong;
      this.elements.feedback.innerHTML = `<strong>${heading}</strong><span>${this.escapeHtml(feedbackText)}</span>`;
      this.elements.feedback.hidden = false;
      this.elements.nextButton.hidden = false;
      this.elements.nextButton.textContent = this.state.index === this.questions.length - 1 ? 'Visa mitt resultat' : 'Nästa fråga';
      this.elements.nextButton.focus({ preventScroll: true });
      this.announce(`${heading} ${feedbackText}`);

      pushEvent('nolia_quiz_answer', {
        question_id: question.id,
        correct: isCorrect,
        question_number: this.state.index + 1
      });
    }

    next() {
      if (!this.state.answered) return;
      this.state.index += 1;
      if (this.state.index >= this.questions.length) {
        this.complete();
      } else {
        this.renderQuestion();
      }
    }

    complete() {
      this.state.completed = true;
      const level = formatResultLevel(this.state.score);
      this.elements.questionCard.hidden = true;
      this.elements.resultCard.hidden = false;
      this.elements.resultScore.textContent = `Du fick ${this.state.score} av ${this.questions.length} rätt`;
      this.elements.resultTitle.textContent = `${level}!`;
      this.elements.resultCopy.textContent = resultCopy(level);

      const scoreField = this.elements.form.querySelector('[name="quiz_score"]');
      const levelField = this.elements.form.querySelector('[name="result_level"]');
      const completedField = this.elements.form.querySelector('[name="completed_at"]');
      if (scoreField) scoreField.value = String(this.state.score);
      if (levelField) levelField.value = level;
      if (completedField) completedField.value = new Date().toISOString();

      this.elements.resultCard.scrollIntoView({ behavior: 'smooth', block: 'start' });
      this.announce(`Du fick ${this.state.score} av ${this.questions.length} rätt. ${level}.`);
      pushEvent('nolia_quiz_complete', {
        score: this.state.score,
        result_level: level,
        campaign: 'nolia_pitea_2026_ag_grill'
      });
    }

    captureUtmParameters() {
      const params = new URLSearchParams(window.location.search);
      ['utm_source', 'utm_medium', 'utm_campaign'].forEach((name) => {
        const field = this.elements.form?.querySelector(`[name="${name}"]`);
        if (field) field.value = (params.get(name) || '').slice(0, 180);
      });
      const campaign = this.elements.form?.querySelector('[name="campaign"]');
      if (campaign) campaign.value = 'nolia_pitea_2026_ag_grill';
    }

    async submit(event) {
      event.preventDefault();
      if (!this.state.completed) {
        this.setFormStatus('Du behöver slutföra quizet innan du skickar ditt tävlingsbidrag.', true);
        return;
      }
      if (!this.elements.form.reportValidity()) return;

      if (!this.endpoint) {
        this.setFormStatus('Formuläret är ännu inte anslutet. Be personalen i montern om hjälp.', true);
        return;
      }

      this.elements.submitButton.disabled = true;
      this.elements.submitButton.textContent = 'Skickar…';
      this.setFormStatus('', false);

      const data = Object.fromEntries(new FormData(this.elements.form).entries());
      const payload = {
        firstName: String(data.first_name || '').trim(),
        lastName: String(data.last_name || '').trim(),
        email: String(data.email || '').trim(),
        phone: String(data.phone || '').trim(),
        quizScore: Number(data.quiz_score),
        resultLevel: String(data.result_level || ''),
        completedAt: String(data.completed_at || ''),
        termsConsent: data.terms_consent === 'on',
        marketingConsent: data.marketing_consent === 'on',
        source: String(data.source || 'Noliamässan Piteå 2026'),
        campaign: String(data.campaign || 'nolia_pitea_2026_ag_grill'),
        utmSource: String(data.utm_source || ''),
        utmMedium: String(data.utm_medium || ''),
        utmCampaign: String(data.utm_campaign || ''),
        website: String(data.website || ''),
        pageUrl: window.location.href.slice(0, 500),
        kiosk: this.kiosk
      };

      try {
        const response = await fetch(this.endpoint, {
          method: 'POST',
          mode: 'cors',
          credentials: 'omit',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload)
        });
        const result = await response.json().catch(() => ({}));

        if (!response.ok) {
          if (response.status === 409) throw new Error('Ett tävlingsbidrag med den här e-postadressen är redan registrerat.');
          if (response.status === 410) throw new Error('Tävlingen är avslutad och tar inte längre emot bidrag.');
          if (response.status === 429) throw new Error('För många försök på kort tid. Vänta en stund och försök igen.');
          throw new Error(result.message || 'Tävlingsbidraget kunde inte skickas. Försök igen.');
        }

        this.showThanks();
        pushEvent('nolia_quiz_entry_submitted', {
          score: this.state.score,
          result_level: payload.resultLevel,
          marketing_consent: payload.marketingConsent,
          campaign: payload.campaign
        });
      } catch (error) {
        this.setFormStatus(error.message || 'Ett oväntat fel inträffade. Försök igen.', true);
        this.elements.submitButton.disabled = false;
        this.elements.submitButton.textContent = 'Skicka mitt tävlingsbidrag';
      }
    }

    showThanks() {
      this.elements.resultCard.hidden = true;
      this.elements.thanksCard.hidden = false;
      this.elements.thanksCard.scrollIntoView({ behavior: 'smooth', block: 'start' });
      this.announce('Tack. Ditt tävlingsbidrag är registrerat.');

      if (this.kiosk) {
        window.clearTimeout(this.kioskTimer);
        this.kioskTimer = window.setTimeout(() => this.resetForNextParticipant(), this.kioskResetSeconds * 1000);
      }
    }

    resetForNextParticipant() {
      window.clearTimeout(this.kioskTimer);
      this.elements.form?.reset();
      this.captureUtmParameters();
      this.state = { index: 0, score: 0, answered: false, completed: false };
      this.formStarted = false;
      this.elements.thanksCard.hidden = true;
      this.elements.resultCard.hidden = true;
      this.elements.questionCard.hidden = true;
      this.setFormStatus('', false);
      this.elements.submitButton.disabled = false;
      this.elements.submitButton.textContent = 'Skicka mitt tävlingsbidrag';
      this.elements.startButton.hidden = false;
      this.elements.quizSection.hidden = true;
      window.scrollTo({ top: this.root.offsetTop, behavior: 'smooth' });
      window.setTimeout(() => this.elements.startButton.focus({ preventScroll: true }), 350);
    }

    setFormStatus(message, isError) {
      this.elements.formStatus.textContent = message;
      this.elements.formStatus.classList.toggle('is-error', Boolean(isError));
      if (message) this.announce(message);
    }

    announce(message) {
      if (!this.elements.live) return;
      this.elements.live.textContent = '';
      window.setTimeout(() => { this.elements.live.textContent = message; }, 20);
    }

    escapeHtml(value) {
      const span = document.createElement('span');
      span.textContent = value || '';
      return span.innerHTML;
    }
  }

  const init = () => document.querySelectorAll(SELECTOR).forEach((root) => {
    if (!root.dataset.initialized) {
      root.dataset.initialized = 'true';
      new NoliaQuiz(root);
    }
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', init);
})();
